import type React from "react";
import { useEffect } from "react";
import { UI_ICONS } from "../../icons";

interface ModalProps {
	title: string;
	isOpen: boolean;
	onClose: () => void;
	children: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({
	title,
	isOpen,
	onClose,
	children,
}) => {
	useEffect(() => {
		const handleEsc = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		if (isOpen) {
			window.addEventListener("keydown", handleEsc);
			document.body.style.overflow = "hidden";
		}
		return () => {
			window.removeEventListener("keydown", handleEsc);
			document.body.style.overflow = "";
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		// biome-ignore lint/a11y/useSemanticElements: Modal overlay provides clickable area to close modal
		<div
			className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
			onClick={onClose}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					onClose();
				}
			}}
			role="button"
			tabIndex={-1}
			aria-label="閉じる"
		>
			<div
				className="bg-card-bg border border-border-main rounded-2xl w-full max-w-[800px] max-h-[90dvh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-300
					max-[600px]:max-h-[95dvh] max-[600px]:rounded-xl"
				onClick={(e) => e.stopPropagation()}
				onKeyDown={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
				tabIndex={-1}
			>
				<div className="px-6 py-5 border-b border-border-main flex justify-between items-center">
					<h2 className="m-0 text-xl text-text-main font-semibold">{title}</h2>
					<button
						type="button"
						className="bg-transparent border-none text-text-dim text-2xl cursor-pointer p-1 flex items-center justify-center transition hover:text-text-main focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded-lg active:scale-95"
						onClick={onClose}
						aria-label="閉じる"
					>
						{UI_ICONS.CLOSE}
					</button>
				</div>
				<div className="flex-1 overflow-y-auto p-6">{children}</div>
			</div>
		</div>
	);
};
